import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  AlertTriangle,
  Trash2,
  Pencil,
  Search,
  Loader2,
  ShieldCheck,
  X,
  AlertCircle,
  ShieldAlert,
  CheckCircle2,
  CircleOff,
  Plus,
} from "lucide-react";
import {
  Modal,
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
  ModalFooterButtons,
} from "../components/Modal";
import { DataTable, type Column } from "../components/DataTable";
import { Dropdown } from "../components/Dropdown";
import { RBACAction } from "../components/RBACButton";
import { showToast } from "../components/ui/custom-toast";
import { getAPI, postAPI, putAPI, deleteAPI } from "../../api/frameworksApi";
import { API_URL } from "../../constants";
import { validateMaxLength, validateRequired } from "../../utils/validators";

// ─── Types ──────────────────────────────────────────────────────────────────

type RuleStatus = "ACTIVE" | "INACTIVE";
type StatusFilter = "ALL" | RuleStatus;
type RuleSeverity = "MANDATORY" | "STANDARD";
type RuleKind = "SYSTEM" | "CUSTOM";

interface OrgUnit {
  id: string;
  name: string;
  shortName?: string;
  orgType?: string;
  isActive?: boolean;
}

type RuleParams =
  | { aliases?: string[] }
  | { pattern?: string; case_insensitive?: boolean }
  | { min?: number }
  | { min_match_ratio?: number }
  | Record<string, unknown>;

interface ValidationRule {
  id: string;
  orgId: string;
  kind: RuleKind;
  ruleCode: string;
  title: string;
  validatorType: string;
  target: string;
  params: RuleParams;
  severity: RuleSeverity;
  remediation?: string;
  category?: string;
  enabled: boolean;
  status: RuleStatus;
  createdAt: string;
  updatedAt: string;
}

interface ApiErrorEntry {
  code: string;
  title?: string;
  detail: string;
  pointer?: string;
}

interface ParsedApiError {
  banner: string;
  fieldErrors: Record<string, string>;
}

// ─── Local storage keys ─────────────────────────────────────────────────────

const LS_ORG_KEY = "validation-rules:org";
const LS_STATUS_KEY = "validation-rules:status";

// ─── Helpers ────────────────────────────────────────────────────────────────

function pointerToFieldName(pointer?: string): string | undefined {
  if (!pointer) return undefined;
  const parts = pointer.split("/").filter(Boolean);
  return parts.length ? parts[parts.length - 1] : undefined;
}

const ERROR_CODE_MESSAGES: Record<string, string> = {
  "GOV-0030-E": "Validation rule not found.",
  "GOV-0031-E": "An organization can't be changed after a rule is created.",
};

function parseApiError(err: any): ParsedApiError {
  const fieldErrors: Record<string, string> = {};
  const errors: ApiErrorEntry[] = err?.response?.data?.errors ?? [];

  if (errors.length === 0) {
    return {
      banner:
        err?.message === "OFFLINE"
          ? "You appear to be offline. Check your connection and try again."
          : (err?.message ?? "Something went wrong. Please try again."),
      fieldErrors,
    };
  }

  let banner = "";
  for (const e of errors) {
    if (e.code === "SYS-0002-E") {
      const f = pointerToFieldName(e.pointer);
      if (f) {
        fieldErrors[f] = e.detail;
        continue;
      }
    }
    if (!banner) banner = ERROR_CODE_MESSAGES[e.code] ?? e.detail;
  }
  if (!banner && Object.keys(fieldErrors).length > 0) {
    banner = "Please fix the highlighted fields and try again.";
  }
  return { banner, fieldErrors };
}

// Shallow equality for params object — used to detect "no changes"
function paramsEqual(a: RuleParams, b: RuleParams): boolean {
  return JSON.stringify(a ?? {}) === JSON.stringify(b ?? {});
}

/**
 * Returns true when the rule's params satisfy the minimum requirements
 * for its validatorType. Used to flag rules that need setup before they
 * can do useful work.
 */
function isParamsConfigured(
  validatorType: string,
  params: RuleParams,
): boolean {
  const p = (params ?? {}) as any;
  switch (validatorType) {
    case "synonym_contains":
      return Array.isArray(p.aliases) && p.aliases.length > 0;
    case "regex_match":
      return typeof p.pattern === "string" && p.pattern.trim().length > 0;
    case "count_gte":
      return typeof p.min === "number" && p.min > 0;
    case "text_consistency":
      return typeof p.min_match_ratio === "number" && p.min_match_ratio > 0;
    case "structure_exists":
    case "date_present":
      return true;
    default:
      return true;
  }
}

// ─── Visual helpers ─────────────────────────────────────────────────────────

/** Consistent pill geometry used by every badge in this page.
 *  `min-w-[88px]` ensures every badge in the same column renders at the same width
 *  regardless of label length (e.g. "Mandatory" vs "Standard"). */
const BADGE_BASE =
  "inline-flex items-center justify-center min-w-[88px] text-[10px] font-bold tracking-wide px-2.5 py-1 rounded-full border whitespace-nowrap leading-none";

function SeverityBadge({ severity }: { severity: RuleSeverity }) {
  const isMandatory = severity === "MANDATORY";
  return (
    <span
      className={`${BADGE_BASE} ${
        isMandatory
          ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20"
          : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20"
      }`}
    >
      {isMandatory ? "Mandatory" : "Standard"}
    </span>
  );
}

/** Same pill geometry as `BADGE_BASE` but ring-based (no border) so the
 *  category badge matches the Content Registry document-type palette. */
const CATEGORY_BADGE_BASE =
  "inline-flex items-center justify-center min-w-[88px] text-[10px] font-bold capitalize tracking-wide px-2.5 py-1 rounded-full whitespace-nowrap leading-none";

/** Category colours mirror the Content Registry document-type palette
 *  (bg + text + ring), so the same hues are reused across both pages. */
const CATEGORY_COLORS: Record<string, string> = {
  metadata:
    "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 ring-1 ring-blue-200/60 dark:ring-blue-500/20",
  structural:
    "bg-fuchsia-50 dark:bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300 ring-1 ring-fuchsia-200/60 dark:ring-fuchsia-500/20",
  semantic:
    "bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300 ring-1 ring-violet-200/60 dark:ring-violet-500/20",
  content:
    "bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300 ring-1 ring-orange-200/60 dark:ring-orange-500/20",
};
const DEFAULT_CATEGORY_COLOR =
  "bg-slate-100 dark:bg-white/[0.05] text-slate-700 dark:text-white/60 ring-1 ring-slate-200/60 dark:ring-white/[0.08]";

function CategoryBadge({ category }: { category?: string }) {
  if (!category)
    return (
      <span className="text-[11px] text-[#94a3b8] dark:text-white/25">—</span>
    );
  const cls =
    CATEGORY_COLORS[category.toLowerCase()] ?? DEFAULT_CATEGORY_COLOR;
  return <span className={`${CATEGORY_BADGE_BASE} ${cls}`}>{category}</span>;
}

function StatusBadge({ enabled }: { enabled: boolean }) {
  return (
    <span
      className={`${BADGE_BASE} ${
        enabled
          ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20"
          : "bg-slate-100 text-slate-500 border-slate-200 dark:bg-white/[0.04] dark:text-white/40 dark:border-white/[0.08]"
      }`}
    >
      {enabled ? "Active" : "Inactive"}
    </span>
  );
}

/**
 * Summarises a rule's parameters for the table. Empty / unset values surface
 * a clear "Not set" affordance the user can click to open the editor.
 */
function ParamsSummary({
  rule,
  onConfigure,
}: {
  rule: ValidationRule;
  onConfigure: () => void;
}) {
  if (rule.kind === "CUSTOM") {
    return (
      <span className="text-[11px] italic text-[#94a3b8] dark:text-white/30">
        —
      </span>
    );
  }
  const p = (rule.params ?? {}) as any;
  const notSet = (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onConfigure();
      }}
      className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-700 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 underline underline-offset-2 decoration-dotted"
    >
      <AlertCircle className="size-3" />
      Not set — configure
    </button>
  );

  switch (rule.validatorType) {
    case "synonym_contains": {
      const aliases: string[] = Array.isArray(p.aliases) ? p.aliases : [];
      if (aliases.length === 0) return notSet;
      const shown = aliases.slice(0, 3);
      const extra = aliases.length - shown.length;
      return (
        <div
          className="flex flex-wrap items-center gap-1"
          title={aliases.join(", ")}
        >
          {shown.map((a) => (
            <span
              key={a}
              className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/20"
            >
              {a}
            </span>
          ))}
          {extra > 0 && (
            <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-[#f1f5f9] dark:bg-white/[0.05] text-[#64748b] dark:text-white/45 border border-[#e2e8f0] dark:border-white/[0.07]">
              +{extra}
            </span>
          )}
        </div>
      );
    }

    case "regex_match": {
      const pattern = String(p.pattern ?? "").trim();
      if (!pattern) return notSet;
      return (
        <div className="flex items-center gap-1.5 min-w-0">
          <code
            className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-[#f1f5f9] dark:bg-white/[0.06] text-[#0f172a] dark:text-white/80 truncate max-w-[180px]"
            title={pattern}
          >
            {pattern}
          </code>
          {p.case_insensitive && (
            <span
              className="text-[10px] font-bold text-[#64748b] dark:text-white/45"
              title="Case insensitive"
            >
              /i
            </span>
          )}
        </div>
      );
    }

    case "count_gte": {
      if (typeof p.min !== "number" || p.min <= 0) return notSet;
      return (
        <span className="text-[12px] font-mono text-[#0f172a] dark:text-white/80">
          ≥ {p.min}
        </span>
      );
    }

    case "text_consistency": {
      if (typeof p.min_match_ratio !== "number" || p.min_match_ratio <= 0)
        return notSet;
      return (
        <span className="text-[12px] font-mono text-[#0f172a] dark:text-white/80">
          ≥ {Math.round(p.min_match_ratio * 100)}%
        </span>
      );
    }

    case "structure_exists":
    case "date_present":
      return (
        <span className="text-[11px] italic text-[#94a3b8] dark:text-white/30">
          No parameters
        </span>
      );

    default:
      return (
        <span className="text-[11px] italic text-[#94a3b8] dark:text-white/30">
          —
        </span>
      );
  }
}

// ─── Radio card (used for severity + status selectors) ─────────────────────

type RadioTone = "red" | "amber" | "emerald" | "slate" | "indigo";

const RADIO_TONE: Record<
  RadioTone,
  { active: string; iconActive: string; iconIdle: string }
> = {
  red: {
    active:
      "border-red-500 bg-red-50 dark:bg-red-500/10 dark:border-red-500/60",
    iconActive: "text-red-600 dark:text-red-400",
    iconIdle: "text-[#94a3b8] dark:text-white/40",
  },
  amber: {
    active:
      "border-amber-500 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/60",
    iconActive: "text-amber-600 dark:text-amber-400",
    iconIdle: "text-[#94a3b8] dark:text-white/40",
  },
  emerald: {
    active:
      "border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/60",
    iconActive: "text-emerald-600 dark:text-emerald-400",
    iconIdle: "text-[#94a3b8] dark:text-white/40",
  },
  slate: {
    active:
      "border-slate-400 bg-slate-50 dark:bg-white/[0.06] dark:border-white/30",
    iconActive: "text-slate-600 dark:text-white/80",
    iconIdle: "text-[#94a3b8] dark:text-white/40",
  },
  indigo: {
    active:
      "border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 dark:border-indigo-500/60",
    iconActive: "text-indigo-600 dark:text-indigo-400",
    iconIdle: "text-[#94a3b8] dark:text-white/40",
  },
};

function RadioCard({
  selected,
  onClick,
  icon,
  label,
  description,
  tone,
}: {
  selected: boolean;
  onClick: () => void;
  icon: ReactNode;
  label: string;
  description: string;
  tone: RadioTone;
}) {
  const palette = RADIO_TONE[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`relative text-left p-3 rounded-lg border transition-all flex items-start gap-2.5 ${
        selected
          ? `${palette.active} shadow-sm`
          : "border-[#e2e8f0] dark:border-white/[0.07] bg-[#f8fafc] dark:bg-white/[0.02] hover:border-[#cbd5e1] dark:hover:border-white/20"
      }`}
    >
      <span
        className={`mt-0.5 flex-shrink-0 ${selected ? palette.iconActive : palette.iconIdle}`}
      >
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span
          className={`block text-[13px] font-semibold ${selected ? "text-[#0f172a] dark:text-white" : "text-[#475569] dark:text-white/70"}`}
        >
          {label}
        </span>
        <span className="block text-[11px] mt-0.5 text-[#64748b] dark:text-white/45 leading-snug">
          {description}
        </span>
      </span>
      <span
        className={`mt-0.5 size-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
          selected
            ? `${palette.iconActive.replace("text-", "border-")}`
            : "border-[#cbd5e1] dark:border-white/25"
        }`}
      >
        {selected && (
          <span
            className={`size-1.5 rounded-full ${palette.iconActive.replace("text-", "bg-")}`}
          />
        )}
      </span>
    </button>
  );
}

// ─── Aliases (chip) input ───────────────────────────────────────────────────

function AliasesInput({
  values,
  onChange,
  placeholder = "Add term and press Enter…",
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const addDraft = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (values.includes(trimmed)) {
      setDraft("");
      return;
    }
    onChange([...values, trimmed]);
    setDraft("");
  };

  const removeAt = (idx: number) => {
    onChange(values.filter((_, i) => i !== idx));
  };

  const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addDraft();
    } else if (e.key === "Backspace" && !draft && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 w-full min-h-[38px] bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-2 py-1.5 border border-[#e2e8f0] dark:border-white/[0.07] focus-within:border-indigo-500 dark:focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/10"
      onClick={() => inputRef.current?.focus()}
    >
      {values.map((v, i) => (
        <span
          key={`${v}-${i}`}
          className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/20"
        >
          {v}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              removeAt(i);
            }}
            className="text-indigo-500 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-200"
            aria-label={`Remove ${v}`}
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKey}
        onBlur={addDraft}
        placeholder={values.length === 0 ? placeholder : ""}
        className="flex-1 min-w-[120px] bg-transparent outline-none text-sm text-[#0f172a] dark:text-white placeholder:text-[#64748b] dark:placeholder:text-white/25"
      />
    </div>
  );
}

// ─── Typed params editor ────────────────────────────────────────────────────

/** Validators that have no editable parameters — the Parameters section is hidden. */
const NO_PARAM_VALIDATORS = new Set(["structure_exists", "date_present"]);

function ParamsEditor({
  validatorType,
  value,
  onChange,
}: {
  validatorType: string;
  value: RuleParams;
  onChange: (next: RuleParams) => void;
}) {
  switch (validatorType) {
    case "synonym_contains": {
      const aliases = Array.isArray((value as any)?.aliases)
        ? (value as any).aliases
        : [];
      return (
        <FormField
          label="Aliases"
          hint="Terms or phrases the validator will search for (case-insensitive). Press Enter to add."
        >
          <AliasesInput
            values={aliases}
            onChange={(next) => onChange({ aliases: next })}
          />
        </FormField>
      );
    }

    case "regex_match": {
      const pattern = String((value as any)?.pattern ?? "");
      const caseInsensitive = Boolean((value as any)?.case_insensitive);
      return (
        <div className="space-y-3">
          <FormField label="Pattern" hint="A valid regular expression.">
            <input
              type="text"
              value={pattern}
              onChange={(e) =>
                onChange({ ...(value as object), pattern: e.target.value })
              }
              placeholder="e.g. ^Section\\s+\\d+"
              spellCheck={false}
              className="w-full text-sm font-mono bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 text-[#0f172a] dark:text-white placeholder:text-[#64748b] dark:placeholder:text-white/25"
            />
          </FormField>
          <label className="flex items-center gap-2 text-sm text-[#475569] dark:text-white/70 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={caseInsensitive}
              onChange={(e) =>
                onChange({
                  ...(value as object),
                  case_insensitive: e.target.checked,
                })
              }
              className="size-4 rounded border-[#cbd5e1] dark:border-white/20 accent-indigo-600"
            />
            Case insensitive
          </label>
        </div>
      );
    }

    case "count_gte": {
      const min = (value as any)?.min ?? "";
      return (
        <FormField
          label="Minimum count"
          hint="The minimum number of occurrences required."
        >
          <input
            type="number"
            min={0}
            value={min === "" ? "" : Number(min)}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "")
                onChange({ ...(value as object), min: undefined as any });
              else onChange({ ...(value as object), min: Number(raw) });
            }}
            placeholder="e.g. 3"
            className="w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 text-[#0f172a] dark:text-white placeholder:text-[#64748b] dark:placeholder:text-white/25"
          />
        </FormField>
      );
    }

    case "text_consistency": {
      const ratio = (value as any)?.min_match_ratio ?? "";
      return (
        <FormField
          label="Minimum match ratio"
          hint="A value between 0 and 1 (e.g. 0.6 means 60% of pages must match)."
        >
          <input
            type="number"
            min={0}
            max={1}
            step={0.05}
            value={ratio === "" ? "" : Number(ratio)}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "")
                onChange({
                  ...(value as object),
                  min_match_ratio: undefined as any,
                });
              else
                onChange({
                  ...(value as object),
                  min_match_ratio: Number(raw),
                });
            }}
            placeholder="0.0 – 1.0"
            className="w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 text-[#0f172a] dark:text-white placeholder:text-[#64748b] dark:placeholder:text-white/25"
          />
        </FormField>
      );
    }

    default:
      return (
        <FormField
          label="Parameters (JSON)"
          hint="Unknown validator type — edit raw JSON."
        >
          <textarea
            value={JSON.stringify(value ?? {}, null, 2)}
            onChange={(e) => {
              try {
                onChange(JSON.parse(e.target.value || "{}"));
              } catch {
                // ignore parse errors while typing
              }
            }}
            rows={5}
            spellCheck={false}
            className="w-full text-sm font-mono bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 text-[#0f172a] dark:text-white"
          />
        </FormField>
      );
  }
}

// ─── Main component ─────────────────────────────────────────────────────────

export function ValidationRules() {
  // ── Orgs ────────────────────────────────────────────────────────────────
  const [orgs, setOrgs] = useState<OrgUnit[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgsError, setOrgsError] = useState<string | null>(null);

  const [selectedOrgId, setSelectedOrgId] = useState<string>(
    () => localStorage.getItem(LS_ORG_KEY) ?? "",
  );
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    () => (localStorage.getItem(LS_STATUS_KEY) as StatusFilter) ?? "ALL",
  );

  // ── Rules ──────────────────────────────────────────────────────────────
  const [rules, setRules] = useState<ValidationRule[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState<string | null>(null);

  // ── Create modal ───────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const [createOrgId, setCreateOrgId] = useState<string>("");
  const [createTitle, setCreateTitle] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [createSeverity, setCreateSeverity] =
    useState<RuleSeverity>("MANDATORY");
  const [createAliases, setCreateAliases] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // ── Edit modal ─────────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<ValidationRule | null>(null);
  const [formParams, setFormParams] = useState<RuleParams>({});
  const [formSeverity, setFormSeverity] = useState<RuleSeverity>("MANDATORY");
  const [formEnabled, setFormEnabled] = useState<boolean>(true);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // ── Delete modal ───────────────────────────────────────────────────────
  const [showDelete, setShowDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ValidationRule | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // ── Table search + sort + pagination ──────────────────────────────────
  const [ruleSearch, setRuleSearch] = useState("");
  const [rulePage, setRulePage] = useState(0);
  const [rulePageSize, setRulePageSize] = useState(10);
  const [ruleSortKey, setRuleSortKey] = useState<string | null>("ruleCode");
  const [ruleSortDir, setRuleSortDir] = useState<"asc" | "desc">("asc");
  const handleRuleSort = (key: string) => {
    if (ruleSortKey === key)
      setRuleSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setRuleSortKey(key);
      setRuleSortDir("asc");
      setRulePage(0);
    }
  };

  // ── Fetch orgs once ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOrgsLoading(true);
      setOrgsError(null);
      try {
        const res = await getAPI(API_URL.ORG_UNITS + "?flat=true");
        if (cancelled) return;
        const raw: OrgUnit[] = res?.data ?? [];
        const list = raw.filter((o) => o.orgType === "ORGANIZATION");
        setOrgs(list);
        const stored = localStorage.getItem(LS_ORG_KEY);
        const initial =
          stored && list.some((o) => o.id === stored)
            ? stored
            : (list[0]?.id ?? "");
        setSelectedOrgId(initial);
      } catch (e: any) {
        if (cancelled) return;
        setOrgsError(
          e?.message === "OFFLINE"
            ? "You appear to be offline."
            : "Failed to load organizations.",
        );
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Fetch rules whenever org changes (status is filtered client-side from `enabled`) ──
  const fetchRules = useCallback(async () => {
    if (!selectedOrgId) {
      setRules([]);
      return;
    }
    setRulesLoading(true);
    setRulesError(null);
    try {
      const params = new URLSearchParams({ orgId: selectedOrgId });
      const res = await getAPI(
        `${API_URL.VALIDATION_RULES}?${params.toString()}`,
      );
      setRules(res?.data ?? []);
    } catch (e: any) {
      setRulesError(
        e?.message === "OFFLINE"
          ? "You appear to be offline."
          : "Failed to load validation rules.",
      );
    } finally {
      setRulesLoading(false);
    }
  }, [selectedOrgId]);

  useEffect(() => {
    void fetchRules();
  }, [fetchRules]);

  // ── Persist filter selections ──────────────────────────────────────────
  useEffect(() => {
    if (selectedOrgId) localStorage.setItem(LS_ORG_KEY, selectedOrgId);
  }, [selectedOrgId]);
  useEffect(() => {
    localStorage.setItem(LS_STATUS_KEY, statusFilter);
  }, [statusFilter]);

  // ── Open Create ────────────────────────────────────────────────────────
  const openCreate = () => {
    setCreateOrgId(selectedOrgId);
    setCreateTitle("");
    setCreateDescription("");
    setCreateSeverity("MANDATORY");
    setCreateAliases([]);
    setCreateError(null);
    setShowCreate(true);
  };

  const handleCreate = async () => {
    const title = createTitle.trim();
    const description = createDescription.trim();
    if (!createOrgId || !title || !description) {
      setCreateError("Organization, title, and description are required.");
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const payload: Record<string, any> = {
        orgId: createOrgId,
        kind: "CUSTOM",
        title,
        description,
        severity: createSeverity,
      };
      if (createAliases.length > 0) {
        payload.params = { aliases: createAliases };
      }
      await postAPI(API_URL.VALIDATION_RULES, payload);
      showToast("success", `"${title}" created.`);
      setShowCreate(false);
      if (createOrgId === selectedOrgId) {
        void fetchRules();
      } else {
        setSelectedOrgId(createOrgId);
      }
    } catch (e: any) {
      const parsed = parseApiError(e);
      setCreateError(parsed.banner);
    } finally {
      setCreating(false);
    }
  };

  // ── Open Edit ──────────────────────────────────────────────────────────
  const openEdit = (rule: ValidationRule) => {
    setEditTarget(rule);
    setFormParams(rule.params ?? {});
    setFormSeverity(rule.severity);
    setFormEnabled(rule.enabled);
    setFormError(null);
    setShowForm(true);
  };

  // ── Save (params / severity / status) ──────────────────────────────────
  const handleSave = async () => {
    if (!editTarget) return;
    setFormError(null);

    const body: Record<string, any> = {};
    const hasEditableParams =
      editTarget.kind !== "CUSTOM" &&
      !NO_PARAM_VALIDATORS.has(editTarget.validatorType);
    if (
      hasEditableParams &&
      !paramsEqual(formParams, editTarget.params ?? {})
    ) {
      body.params = formParams;
    }
    if (formSeverity !== editTarget.severity) body.severity = formSeverity;
    if (formEnabled !== editTarget.enabled) body.enabled = formEnabled;

    if (Object.keys(body).length === 0) {
      showToast("warning", "No changes to save.");
      setShowForm(false);
      return;
    }

    setFormSaving(true);
    try {
      await putAPI(`${API_URL.VALIDATION_RULES}/${editTarget.id}`, body);
      showToast("success", `"${editTarget.title}" updated.`);
      setShowForm(false);
      await fetchRules();
    } catch (e: any) {
      const parsed = parseApiError(e);
      setFormError(parsed.banner);
    } finally {
      setFormSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────
  const openDelete = (rule: ValidationRule) => {
    setDeleteTarget(rule);
    setDeleteError(null);
    setShowDelete(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    const previous = rules;
    setRules((prev) => prev.filter((r) => r.id !== deleteTarget.id));
    try {
      await deleteAPI(`${API_URL.VALIDATION_RULES}/${deleteTarget.id}`);
      showToast("success", "Rule deleted.");
      setShowDelete(false);
      void fetchRules();
    } catch (e: any) {
      setRules(previous);
      const parsed = parseApiError(e);
      setDeleteError(parsed.banner);
    } finally {
      setDeleting(false);
    }
  };

  // Reset page when filters change
  useEffect(() => {
    setRulePage(0);
  }, [selectedOrgId, statusFilter, ruleSearch, rulePageSize]);

  // Filter (status from `enabled`, plus free-text search)
  const filteredRules = useMemo(() => {
    const q = ruleSearch.trim().toLowerCase();
    return rules.filter((r) => {
      if (statusFilter === "ACTIVE" && !r.enabled) return false;
      if (statusFilter === "INACTIVE" && r.enabled) return false;
      if (!q) return true;
      return (
        r.title.toLowerCase().includes(q) ||
        r.ruleCode.toLowerCase().includes(q) ||
        r.validatorType.toLowerCase().includes(q) ||
        (r.target ?? "").toLowerCase().includes(q) ||
        (r.category ?? "").toLowerCase().includes(q)
      );
    });
  }, [rules, ruleSearch, statusFilter]);

  const sortedRules = useMemo(() => {
    if (!ruleSortKey) return filteredRules;
    return [...filteredRules].sort((a, b) => {
      const aVal = (a as any)[ruleSortKey] ?? "";
      const bVal = (b as any)[ruleSortKey] ?? "";
      if (typeof aVal === "number" && typeof bVal === "number")
        return ruleSortDir === "asc" ? aVal - bVal : bVal - aVal;
      return ruleSortDir === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredRules, ruleSortKey, ruleSortDir]);

  const paginatedRules = useMemo(
    () =>
      sortedRules.slice(rulePage * rulePageSize, (rulePage + 1) * rulePageSize),
    [sortedRules, rulePage, rulePageSize],
  );

  // ── Table columns ──────────────────────────────────────────────────────
  const columns: Column<ValidationRule>[] = useMemo(
    () => [
      {
        key: "sno",
        header: "#",
        align: "center",
        render: (_row, idx) => (
          <span className="text-xs font-medium d">
            {rulePage * rulePageSize + idx + 1}
          </span>
        ),
      },
      {
        key: "title",
        header: "Title",
        align: "left",
        sortable: true,
        ellipsis: 2,
        tooltip: (r) => r.title,
        render: (r) => (
          <div className="space-y-0.5">
            <p className="text-[13px] font-medium text-[#0f172a] dark:text-white/85">
              {r.title}
            </p>
            {r.remediation && (
              <p className="text-[11px] text-[#94a3b8] dark:text-white/35 line-clamp-1">
                {r.remediation}
              </p>
            )}
          </div>
        ),
      },
      {
        key: "params",
        header: "Parameters",
        align: "left",
        render: (r) => (
          <ParamsSummary rule={r} onConfigure={() => openEdit(r)} />
        ),
      },
      {
        key: "category",
        header: "Category",
        align: "center",
        sortable: true,
        render: (r) => <CategoryBadge category={r.category} />,
      },
      {
        key: "severity",
        header: "Severity",
        align: "center",
        sortable: true,
        render: (r) => <SeverityBadge severity={r.severity} />,
      },
      {
        key: "enabled",
        header: "Status",
        align: "center",
        sortable: true,
        render: (r) => <StatusBadge enabled={r.enabled} />,
      },
      {
        key: "actions",
        header: "Actions",
        align: "center",
        render: (r) => (
          <div className="flex items-center justify-center gap-1.5">
            <RBACAction module="validation_rule" action="update">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(r);
                }}
                title="Edit rule"
                className="p-1.5 rounded-md text-[#475569] dark:text-white/50 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
              >
                <Pencil className="size-3.5" />
              </button>
            </RBACAction>
            <RBACAction module="validation_rule" action="delete">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openDelete(r);
                }}
                title="Delete rule"
                className="p-1.5 rounded-md text-[#94a3b8] dark:text-white/30 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
              >
                <Trash2 className="size-3.5" />
              </button>
            </RBACAction>
          </div>
        ),
      },
    ],
    [rulePage, rulePageSize],
  );

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Validation Rules</h1>
          <p className="text-sm d mt-0.5">
            Per-organization rules used to validate uploaded documents. Only
            rules with status <span className="font-medium">ACTIVE</span> are
            enforced.
          </p>
        </div>
        <RBACAction module="validation_rule" action="create">
          <button
            type="button"
            onClick={openCreate}
            disabled={orgsLoading || orgs.length === 0}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="size-4" />
            Add Rule
          </button>
        </RBACAction>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 d/50 pointer-events-none" />
          <input
            type="text"
            placeholder="Search by code, title, validator…"
            value={ruleSearch}
            onChange={(e) => {
              setRuleSearch(e.target.value);
              setRulePage(0);
            }}
            className="w-full pl-9 pr-4 py-2 text-sm bg-card border border-border rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:d/50"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 ml-auto">
          {/* Organization filter */}
          <div className="w-[240px] flex-shrink-0">
            <FormSelect
              value={selectedOrgId}
              onChange={(v) => setSelectedOrgId(v)}
              placeholder={
                orgsLoading ? "Loading organizations…" : "Select organization"
              }
              options={
                orgsLoading
                  ? [{ value: "", label: "Loading…" }]
                  : orgs.length === 0
                    ? [{ value: "", label: "No organizations" }]
                    : orgs.map((o) => ({ value: o.id, label: o.name }))
              }
            />
          </div>

          {/* Status filter */}
          <div className="w-[160px] flex-shrink-0">
            <FormSelect
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as StatusFilter)}
              options={[
                { value: "ALL", label: "All status" },
                { value: "ACTIVE", label: "Active" },
                { value: "INACTIVE", label: "Inactive" },
              ]}
            />
          </div>

          {/* Rows per page */}
          <div className="flex items-center gap-2">
            <label className="text-sm d whitespace-nowrap">Rows per page</label>
            <Dropdown
              value={String(rulePageSize)}
              onChange={(v) => {
                setRulePageSize(Number(v));
                setRulePage(0);
              }}
              options={[10, 25, 50, 100].map((n) => ({
                value: String(n),
                label: String(n),
              }))}
              size="sm"
              align="end"
              className="!min-w-[80px]"
            />
          </div>
        </div>
      </div>

      {/* Org-level error */}
      {orgsError && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400">
          <AlertTriangle className="size-4 flex-shrink-0" />
          <p className="text-sm flex-1">{orgsError}</p>
        </div>
      )}

      {/* Rules table */}
      <DataTable
        columns={columns}
        data={paginatedRules}
        loading={rulesLoading}
        error={rulesError}
        rowKey={(r) => r.id}
        emptyText={
          !selectedOrgId
            ? "Select an organization to view its rules."
            : ruleSearch.trim()
              ? "No rules match your search."
              : "No validation rules for this organization."
        }
        emptyIcon={
          <ShieldCheck className="size-4 text-[#64748b] dark:text-white/40" />
        }
        page={rulePage}
        pageSize={rulePageSize}
        totalItems={sortedRules.length}
        onPageChange={(p) => setRulePage(p)}
        itemLabel="rules"
        sortKey={ruleSortKey}
        sortDir={ruleSortDir}
        onSort={handleRuleSort}
      />

      {/* ──────────────────────────────────────────────────────────────────
          Create Modal — CUSTOM rules
      ────────────────────────────────────────────────────────────────── */}
      <Modal
        open={showCreate}
        onClose={() => !creating && setShowCreate(false)}
        title="Add Custom Validation Rule"
        description="Custom rules run alongside system rules for the selected organization."
        size="lg"
        closable={!creating}
        footer={
          <ModalFooterButtons
            onCancel={() => setShowCreate(false)}
            onSubmit={handleCreate}
            submitLabel={creating ? "Creating…" : "Create rule"}
            submitDisabled={creating}
          />
        }
      >
        <div className="space-y-4">
          {createError && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400">
              <AlertTriangle className="size-4 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{createError}</p>
            </div>
          )}

          <FormField label="Organization" required>
            <FormSelect
              value={createOrgId}
              onChange={(v) => setCreateOrgId(v)}
              placeholder={
                orgsLoading ? "Loading organizations…" : "Select organization"
              }
              options={orgs.map((o) => ({ value: o.id, label: o.name }))}
            />
          </FormField>

          <FormField label="Title" required>
            <FormInput
              value={createTitle}
              onChange={setCreateTitle}
              placeholder="e.g. Acceptance criteria present"
            />
          </FormField>

          <FormField
            label="Description"
            required
            hint="A short explanation of what this rule checks."
          >
            <FormTextarea
              value={createDescription}
              onChange={setCreateDescription}
              rows={4}
              placeholder="e.g. Verifies each user story includes a list of acceptance criteria."
            />
          </FormField>

          <div>
            <p className="text-sm font-semibold text-foreground mb-2">
              Parameters
            </p>
            <FormField
              label="Aliases"
              hint="Terms or phrases the validator will search for (case-insensitive). Press Enter to add."
            >
              <AliasesInput
                values={createAliases}
                onChange={setCreateAliases}
              />
            </FormField>
          </div>

          <FormField label="Severity" required>
            <div className="grid grid-cols-2 gap-2">
              <RadioCard
                selected={createSeverity === "MANDATORY"}
                onClick={() => setCreateSeverity("MANDATORY")}
                icon={<ShieldAlert className="size-4" />}
                label="Mandatory"
                description="Must pass for the document to be valid."
                tone="red"
              />
              <RadioCard
                selected={createSeverity === "STANDARD"}
                onClick={() => setCreateSeverity("STANDARD")}
                icon={<ShieldCheck className="size-4" />}
                label="Standard"
                description="Recommended check; failures are warnings."
                tone="amber"
              />
            </div>
          </FormField>
        </div>
      </Modal>

      {/* ──────────────────────────────────────────────────────────────────
          Edit Modal — SYSTEM rules: only params / severity / status
      ────────────────────────────────────────────────────────────────── */}
      <Modal
        open={showForm}
        onClose={() => !formSaving && setShowForm(false)}
        title={
          editTarget
            ? `${editTarget.ruleCode} — ${editTarget.title}`
            : "Edit Rule"
        }
        description="Adjust parameters, severity, or status. Other fields are managed by the system."
        size="lg"
        closable={!formSaving}
        footer={
          <ModalFooterButtons
            onCancel={() => setShowForm(false)}
            onSubmit={handleSave}
            submitLabel={formSaving ? "Saving…" : "Save changes"}
            submitDisabled={formSaving}
          />
        }
      >
        {editTarget && (
          <div className="space-y-4">
            {formError && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400">
                <AlertTriangle className="size-4 flex-shrink-0 mt-0.5" />
                <p className="text-sm">{formError}</p>
              </div>
            )}

            {/* Read-only context */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <p className="text-[11px] font-medium tracking-wide text-[#94a3b8] dark:text-white/40">
                  Category
                </p>
                <CategoryBadge category={editTarget.category} />
              </div>
            </div>

            {editTarget.remediation && (
              <div className="space-y-1">
                <p className="text-[11px] font-medium tracking-wide text-[#94a3b8] dark:text-white/40">
                  Remediation
                </p>
                <p className="text-[13px] text-[#475569] dark:text-white/60 leading-relaxed">
                  {editTarget.remediation}
                </p>
              </div>
            )}

            <div className="border-t border-[#e2e8f0] dark:border-white/[0.06]" />

            {/* Editable: params (hidden for CUSTOM rules and validators with no params) */}
            {editTarget.kind !== "CUSTOM" &&
              !NO_PARAM_VALIDATORS.has(editTarget.validatorType) && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-foreground">
                      Parameters
                    </p>
                    {!isParamsConfigured(
                      editTarget.validatorType,
                      formParams,
                    ) && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20">
                        <AlertCircle className="size-2.5" />
                        Needs setup
                      </span>
                    )}
                  </div>
                  <ParamsEditor
                    validatorType={editTarget.validatorType}
                    value={formParams}
                    onChange={setFormParams}
                  />
                </div>
              )}

            {/* Editable: severity (radio cards) */}
            <FormField label="Severity" required>
              <div className="grid grid-cols-2 gap-2">
                <RadioCard
                  selected={formSeverity === "MANDATORY"}
                  onClick={() => setFormSeverity("MANDATORY")}
                  icon={<ShieldAlert className="size-4" />}
                  label="Mandatory"
                  description="Must pass for the document to be valid."
                  tone="red"
                />
                <RadioCard
                  selected={formSeverity === "STANDARD"}
                  onClick={() => setFormSeverity("STANDARD")}
                  icon={<ShieldCheck className="size-4" />}
                  label="Standard"
                  description="Recommended check; failures are warnings."
                  tone="amber"
                />
              </div>
            </FormField>

            {/* Editable: status (driven by the `enabled` boolean) */}
            <FormField label="Status" required>
              <div className="grid grid-cols-2 gap-2">
                <RadioCard
                  selected={formEnabled}
                  onClick={() => setFormEnabled(true)}
                  icon={<CheckCircle2 className="size-4" />}
                  label="Active"
                  description="Enforced when validation runs."
                  tone="emerald"
                />
                <RadioCard
                  selected={!formEnabled}
                  onClick={() => setFormEnabled(false)}
                  icon={<CircleOff className="size-4" />}
                  label="Inactive"
                  description="Saved but not enforced."
                  tone="slate"
                />
              </div>
            </FormField>
          </div>
        )}
      </Modal>

      {/* ──────────────────────────────────────────────────────────────────
          Delete Confirmation Modal
      ────────────────────────────────────────────────────────────────── */}
      <Modal
        open={showDelete}
        onClose={() => !deleting && setShowDelete(false)}
        title={`Delete "${deleteTarget?.title}"?`}
        size="sm"
        closable={!deleting}
        footer={
          <div className="flex items-center justify-end gap-3 w-full">
            <button
              onClick={() => setShowDelete(false)}
              disabled={deleting}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 transition-colors disabled:opacity-40 shadow-md shadow-red-500/20"
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-[#475569] dark:text-white/60">
            This rule will no longer be enforced and will be removed from the
            list. This cannot be undone from the UI.
          </p>
          {deleteError && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400 text-sm">
              <AlertTriangle className="size-4 flex-shrink-0 mt-0.5" />
              <p>{deleteError}</p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
